import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send, MessageCircle } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { collegeInfo } from "../mock/mock";
import { PageHero } from "./About";

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    const saved = JSON.parse(localStorage.getItem("messages") || "[]");
    saved.push({ ...form, id: Date.now(), submittedAt: new Date().toISOString() });
    localStorage.setItem("messages", JSON.stringify(saved));
    toast({ title: "Message sent", description: "Thank you for reaching out. We'll respond within 24 hours." });
    setForm({ name: "", email: "", phone: "", subject: "", message: "" });
  };

  const infoItems = [
    { icon: MapPin, title: "Address", value: collegeInfo.address },
    { icon: Phone, title: "Phone", value: collegeInfo.phone, href: `tel:${collegeInfo.phone}` },
    { icon: Mail, title: "Email", value: collegeInfo.email, href: `mailto:${collegeInfo.email}` },
    { icon: Clock, title: "Office Hours", value: collegeInfo.hours },
  ];

  return (
    <div>
      <PageHero
        eyebrow="Contact"
        title="We'd love to hear from you"
        subtitle="Have questions about admissions, courses or campus? Send us a message."
        image="https://images.unsplash.com/photo-1686624386665-4cd01b96d0f6"
      />

      <section className="section-y bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-8">
          {/* Info panel */}
          <div className="lg:col-span-1 space-y-4">
            {infoItems.map((it) => (
              <div key={it.title} className="bg-brand-ivory border border-brand-gold/25 rounded-sm p-5 hover-lift">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-full bg-brand-green flex items-center justify-center border border-brand-gold shrink-0">
                    <it.icon className="w-5 h-5 text-brand-gold" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-brand-gold-dark font-bold mb-1">{it.title}</div>
                    {it.href ? (
                      <a href={it.href} className="text-brand-green-deep font-serif-display text-lg hover:text-brand-gold-dark transition-colors">
                        {it.value}
                      </a>
                    ) : (
                      <div className="text-brand-green-deep font-serif-display text-lg">{it.value}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Form */}
          <div className="lg:col-span-2 bg-white border border-brand-gold/30 rounded-sm p-6 md:p-10 shadow-xl">
            <div className="flex items-center gap-3 mb-6">
              <MessageCircle className="w-6 h-6 text-brand-gold-dark" />
              <h2 className="font-serif-display text-3xl text-brand-green-deep">Send us a message</h2>
            </div>
            <form onSubmit={onSubmit} className="grid md:grid-cols-2 gap-4">
              {[
                { name: "name", label: "Your Name", type: "text", required: true },
                { name: "email", label: "Email", type: "email", required: true },
                { name: "phone", label: "Phone", type: "tel" },
                { name: "subject", label: "Subject", type: "text" },
              ].map((f) => (
                <div key={f.name} className="flex flex-col gap-1.5">
                  <label className="text-xs uppercase tracking-widest text-brand-green-deep font-semibold">{f.label}</label>
                  <input
                    name={f.name}
                    type={f.type}
                    required={f.required}
                    value={form[f.name]}
                    onChange={onChange}
                    className="px-4 py-3 bg-brand-ivory border border-brand-gold/30 rounded-sm text-sm text-neutral-800 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-colors"
                  />
                </div>
              ))}
              <div className="md:col-span-2 flex flex-col gap-1.5">
                <label className="text-xs uppercase tracking-widest text-brand-green-deep font-semibold">Message</label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={onChange}
                  className="px-4 py-3 bg-brand-ivory border border-brand-gold/30 rounded-sm text-sm text-neutral-800 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-colors resize-none"
                />
              </div>
              <button type="submit" className="md:col-span-2 btn-primary px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2">
                Send Message <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="rounded-sm overflow-hidden border border-brand-gold/30 shadow-xl">
            <iframe
              title="College Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d243645.63393697417!2d76.71659534863281!3d17.32924700000002!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc8c8b6ffffffff%3A0x0!2sKalaburagi%2C%20Karnataka!5e0!3m2!1sen!2sin!4v1710000000000"
              width="100%"
              height="420"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
